export type TemplateId = "basic-box" | "tcg-card-box" | "cutlery-tray";
export type HandleStyle = "none" | "notch" | "slot";
export type HandleSide = "long" | "short";
export type DividerLayout = "grid" | "staggered";
export type WallFace = "front" | "back" | "left" | "right";
export type DividerAxis = "column" | "row";

export type HandlePlacement = {
  id: string;
  face: WallFace;
  offset: number;
  verticalOffset?: number;
};

export type DividerSegment = {
  id: string;
  axis: DividerAxis;
  fraction: number;
  startFraction: number;
  endFraction: number;
};

export type StorageDesign = {
  schemaVersion: 1;
  templateId: TemplateId;
  widthMm: number;
  depthMm: number;
  heightMm: number;
  wallMm: number;
  bottomMm: number;
  dividerMm: number;
  dividerHeightMm: number;
  columns: number;
  rows: number;
  cornerRadiusMm: number;
  hasBottom: boolean;
  hasFingerNotch: boolean;
  handleStyle: HandleStyle;
  handleSide: HandleSide;
  handleCount: number;
  handleOffset: number;
  handleLengthMm: number;
  handlePlacements: HandlePlacement[];
  dividerLayout: DividerLayout;
  columnFractions: number[];
  rowFractions: number[];
  dividerSegments: DividerSegment[];
};

export const TEMPLATE_LABELS: Record<TemplateId, string> = {
  "basic-box": "ボックス",
  "tcg-card-box": "TCGカード入れ",
  "cutlery-tray": "カトラリー"
};

export const templateOrder: TemplateId[] = ["basic-box", "tcg-card-box", "cutlery-tray"];

export const designLimits = {
  widthMm: { min: 30, max: 350, step: 1 },
  depthMm: { min: 30, max: 350, step: 1 },
  heightMm: { min: 8, max: 350, step: 1 },
  wallMm: { min: 1.2, max: 6, step: 0.2 },
  bottomMm: { min: 0.8, max: 6, step: 0.2 },
  dividerMm: { min: 1, max: 5, step: 0.2 },
  dividerHeightMm: { min: 4, max: 140, step: 1 },
  columns: { min: 1, max: 8, step: 1 },
  rows: { min: 1, max: 8, step: 1 },
  handleOffset: { min: 0.18, max: 0.82, step: 0.01 },
  handleLengthMm: { min: 16, max: 120, step: 1 },
  cornerRadiusMm: { min: 0, max: 18, step: 1 }
} as const;

export function normalizeDesign(design: StorageDesign): StorageDesign {
  const columns = Math.round(clamp(design.columns, designLimits.columns.min, designLimits.columns.max));
  const rows = Math.round(clamp(design.rows, designLimits.rows.min, designLimits.rows.max));
  const widthMm = clamp(finiteNumber(design.widthMm, designLimits.widthMm.min), designLimits.widthMm.min, designLimits.widthMm.max);
  const depthMm = clamp(finiteNumber(design.depthMm, designLimits.depthMm.min), designLimits.depthMm.min, designLimits.depthMm.max);
  const heightMm = clamp(finiteNumber(design.heightMm, designLimits.heightMm.min), designLimits.heightMm.min, designLimits.heightMm.max);
  const wallLimit = Math.min(widthMm, depthMm) / 6;
  const wallMm = clamp(
    finiteNumber(design.wallMm, designLimits.wallMm.min),
    designLimits.wallMm.min,
    Math.min(designLimits.wallMm.max, wallLimit)
  );
  const bottomMm = clamp(
    finiteNumber(design.bottomMm, designLimits.bottomMm.min),
    designLimits.bottomMm.min,
    Math.min(designLimits.bottomMm.max, heightMm / 2)
  );
  const usableHeight = Math.max(designLimits.dividerHeightMm.min, heightMm - (design.hasBottom ? bottomMm : 0));
  const maxRadius = Math.min(widthMm, depthMm) / 2 - wallMm;
  const handleStyle = normalizeHandleStyle(design.handleStyle, design.hasFingerNotch);
  const handleCount = finiteNumber(design.handleCount, 1);
  const handleOffset = finiteNumber(design.handleOffset, 0.5);
  const columnFractions = normalizeFractions(design.columnFractions, columns - 1);
  const rowFractions = normalizeFractions(design.rowFractions, rows - 1);
  const handlePlacements = normalizeHandlePlacements(
    design.handlePlacements,
    handleStyle,
    design.handleSide,
    handleCount,
    handleOffset
  );

  return {
    schemaVersion: 1,
    templateId: design.templateId,
    widthMm,
    depthMm,
    heightMm,
    wallMm,
    bottomMm,
    dividerMm: clamp(
      finiteNumber(design.dividerMm, designLimits.dividerMm.min),
      designLimits.dividerMm.min,
      designLimits.dividerMm.max
    ),
    dividerHeightMm: clamp(
      finiteNumber(design.dividerHeightMm, usableHeight),
      designLimits.dividerHeightMm.min,
      Math.min(designLimits.dividerHeightMm.max, usableHeight)
    ),
    columns,
    rows,
    cornerRadiusMm: clamp(finiteNumber(design.cornerRadiusMm, 0), 0, Math.max(0, maxRadius)),
    hasBottom: design.hasBottom,
    hasFingerNotch: handleStyle !== "none",
    handleStyle,
    handleSide: design.handleSide === "short" ? "short" : "long",
    handleCount: Math.round(clamp(handleCount, 1, 2)),
    handleOffset: clamp(handleOffset, designLimits.handleOffset.min, designLimits.handleOffset.max),
    handleLengthMm: clamp(
      finiteNumber(design.handleLengthMm, 52),
      designLimits.handleLengthMm.min,
      designLimits.handleLengthMm.max
    ),
    handlePlacements,
    dividerLayout: design.dividerLayout === "staggered" ? "staggered" : "grid",
    columnFractions,
    rowFractions,
    dividerSegments: normalizeDividerSegments(
      design.dividerSegments,
      columnFractions,
      rowFractions,
      design.dividerLayout === "staggered" ? "staggered" : "grid"
    )
  };
}

export function getTemplateDesign(templateId: TemplateId): StorageDesign {
  const base: StorageDesign = {
    schemaVersion: 1,
    templateId,
    widthMm: 160,
    depthMm: 110,
    heightMm: 34,
    wallMm: 2,
    bottomMm: 1.8,
    dividerMm: 1.6,
    dividerHeightMm: 32,
    columns: 1,
    rows: 1,
    cornerRadiusMm: 0,
    hasBottom: true,
    hasFingerNotch: false,
    handleStyle: "none",
    handleSide: "long",
    handleCount: 1,
    handleOffset: 0.5,
    handleLengthMm: 52,
    handlePlacements: [],
    dividerLayout: "grid",
    columnFractions: [],
    rowFractions: [],
    dividerSegments: []
  };

  switch (templateId) {
    case "basic-box":
      return {
        ...base,
        widthMm: 190,
        depthMm: 140,
        heightMm: 72,
        hasFingerNotch: true,
        handleStyle: "notch",
        handleSide: "short",
        handlePlacements: [{ id: "handle-right", face: "right", offset: 0.5, verticalOffset: 0.58 }]
      };
    case "tcg-card-box":
      return {
        ...base,
        widthMm: 136,
        depthMm: 120,
        heightMm: 92,
        dividerHeightMm: 60,
        columns: 2,
        rows: 3,
        columnFractions: [0.5],
        rowFractions: [1 / 3, 2 / 3],
        dividerSegments: [
          { id: "divider-column-0", axis: "column", fraction: 0.5, startFraction: 0, endFraction: 1 },
          { id: "divider-row-0", axis: "row", fraction: 1 / 3, startFraction: 0, endFraction: 1 },
          { id: "divider-row-1", axis: "row", fraction: 2 / 3, startFraction: 0, endFraction: 1 }
        ]
      };
    case "cutlery-tray":
      return {
        ...base,
        widthMm: 200,
        depthMm: 160,
        heightMm: 24,
        dividerHeightMm: 22,
        columns: 1,
        rows: 3,
        rowFractions: [1 / 3, 2 / 3],
        dividerSegments: [
          { id: "divider-row-0", axis: "row", fraction: 1 / 3, startFraction: 0, endFraction: 1 },
          { id: "divider-row-1", axis: "row", fraction: 2 / 3, startFraction: 0, endFraction: 1 }
        ]
      };
    default:
      return base;
  }
}

export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
}

export function canonicalDesign(design: StorageDesign): string {
  const normalized = normalizeDesign(design);

  return JSON.stringify({
    schemaVersion: normalized.schemaVersion,
    templateId: normalized.templateId,
    widthMm: normalized.widthMm,
    depthMm: normalized.depthMm,
    heightMm: normalized.heightMm,
    wallMm: normalized.wallMm,
    bottomMm: normalized.bottomMm,
    dividerMm: normalized.dividerMm,
    dividerHeightMm: normalized.dividerHeightMm,
    columns: normalized.columns,
    rows: normalized.rows,
    cornerRadiusMm: normalized.cornerRadiusMm,
    hasBottom: normalized.hasBottom,
    hasFingerNotch: normalized.hasFingerNotch,
    handleStyle: normalized.handleStyle,
    handleSide: normalized.handleSide,
    handleCount: normalized.handleCount,
    handleOffset: normalized.handleOffset,
    handleLengthMm: normalized.handleLengthMm,
    handlePlacements: normalized.handlePlacements,
    dividerLayout: normalized.dividerLayout,
    columnFractions: normalized.columnFractions,
    rowFractions: normalized.rowFractions,
    dividerSegments: normalized.dividerSegments
  });
}

export async function hashDesign(design: StorageDesign): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalDesign(design));
  const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
  const hashBytes = [...new Uint8Array(hashBuffer)];

  return `sha256:${hashBytes.map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

export function getHandleFaces(design: StorageDesign): WallFace[] {
  const d = normalizeDesign(design);

  if (d.handleStyle === "none") {
    return [];
  }

  return d.handlePlacements.map((placement) => placement.face);
}

export function getHandleOffset(design: StorageDesign, face: WallFace): number {
  const d = normalizeDesign(design);

  return d.handlePlacements.find((placement) => placement.face === face)?.offset ?? d.handleOffset;
}

export function getHandleLength(axisLength: number, requestedLength?: number): number {
  const fallback = Math.min(52, axisLength * 0.38);
  const desired = finiteNumber(requestedLength, fallback);
  const maxLength = Math.max(designLimits.handleLengthMm.min, axisLength - 12);

  return clamp(desired, designLimits.handleLengthMm.min, Math.min(designLimits.handleLengthMm.max, maxLength));
}

export function getHandleAxisCenter(axisLength: number, handleLength: number, offset: number): number {
  const halfLength = handleLength / 2;
  const min = -axisLength / 2 + halfLength;
  const max = axisLength / 2 - halfLength;

  return min + (max - min) * clamp(offset, designLimits.handleOffset.min, designLimits.handleOffset.max);
}

function normalizeHandleStyle(value: HandleStyle | undefined, legacyEnabled: boolean): HandleStyle {
  if (value === "slot" || value === "notch" || value === "none") {
    return value;
  }

  return legacyEnabled ? "notch" : "none";
}

function normalizeFractions(values: number[] | undefined, count: number): number[] {
  if (count <= 0) {
    return [];
  }

  const defaults = Array.from({ length: count }, (_, index) => (index + 1) / (count + 1));
  const fractions = defaults.map((fallback, index) =>
    clamp(finiteNumber(values?.[index], fallback), designLimits.handleOffset.min, designLimits.handleOffset.max)
  );
  const sorted = fractions.sort((a, b) => a - b);
  const minGap = 0.08;

  for (let index = 1; index < sorted.length; index += 1) {
    sorted[index] = Math.max(sorted[index], sorted[index - 1] + minGap);
  }

  for (let index = sorted.length - 2; index >= 0; index -= 1) {
    sorted[index] = Math.min(sorted[index], sorted[index + 1] - minGap);
  }

  return sorted.map((fraction) => Number(clamp(fraction, designLimits.handleOffset.min, designLimits.handleOffset.max).toFixed(3)));
}

function finiteNumber(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function normalizeHandlePlacements(
  placements: HandlePlacement[] | undefined,
  style: HandleStyle,
  side: HandleSide | undefined,
  count: number,
  offset: number
): HandlePlacement[] {
  if (style === "none") {
    return [];
  }

  if (placements?.length) {
    const seen = new Set<WallFace>();

    return placements
      .filter((placement) => isWallFace(placement.face))
      .filter((placement) => {
        if (seen.has(placement.face)) {
          return false;
        }

        seen.add(placement.face);
        return true;
      })
      .map((placement) => ({
        id: placement.id || `handle-${placement.face}`,
        face: placement.face,
        offset: clamp(
          finiteNumber(placement.offset, 0.5),
          designLimits.handleOffset.min,
          designLimits.handleOffset.max
        ),
        verticalOffset: clamp(
          finiteNumber(placement.verticalOffset, 0.58),
          designLimits.handleOffset.min,
          designLimits.handleOffset.max
        )
      }));
  }

  const normalizedSide = side === "short" ? "short" : "long";
  const normalizedCount = Math.round(clamp(finiteNumber(count, 1), 1, 2));
  const faces: WallFace[] = normalizedSide === "short"
    ? normalizedCount === 2
      ? ["left", "right"]
      : ["right"]
    : normalizedCount === 2
      ? ["front", "back"]
      : ["front"];

  return faces.map((face) => ({
    id: `handle-${face}`,
    face,
    offset: clamp(finiteNumber(offset, 0.5), designLimits.handleOffset.min, designLimits.handleOffset.max),
    verticalOffset: 0.58
  }));
}

function normalizeDividerSegments(
  segments: DividerSegment[] | undefined,
  columnFractions: number[],
  rowFractions: number[],
  layout: DividerLayout
): DividerSegment[] {
  if (segments?.length) {
    return segments
      .filter((segment) => segment.axis === "column" || segment.axis === "row")
      .map((segment, index) => normalizeDividerSegment(segment, `divider-${index}`));
  }

  const rowBreaks = [0, ...rowFractions, 1];
  const derived: DividerSegment[] = [];

  columnFractions.forEach((fraction, index) => {
    if (layout === "staggered" && rowBreaks.length > 2) {
      for (let segmentIndex = 0; segmentIndex < rowBreaks.length - 1; segmentIndex += 1) {
        const offset = segmentIndex % 2 === 0 ? -0.12 : 0.12;
        derived.push({
          id: `divider-column-${index}-${segmentIndex}`,
          axis: "column",
          fraction: clamp(fraction + offset, 0.14, 0.86),
          startFraction: rowBreaks[segmentIndex],
          endFraction: rowBreaks[segmentIndex + 1]
        });
      }
      return;
    }

    derived.push({
      id: `divider-column-${index}`,
      axis: "column",
      fraction,
      startFraction: 0,
      endFraction: 1
    });
  });

  rowFractions.forEach((fraction, index) => {
    derived.push({
      id: `divider-row-${index}`,
      axis: "row",
      fraction,
      startFraction: 0,
      endFraction: 1
    });
  });

  return derived.map((segment, index) => normalizeDividerSegment(segment, `divider-${index}`));
}

function normalizeDividerSegment(segment: DividerSegment, fallbackId: string): DividerSegment {
  const startFraction = clamp(finiteNumber(segment.startFraction, 0), 0, 0.92);
  const endFraction = clamp(finiteNumber(segment.endFraction, 1), startFraction + 0.08, 1);

  return {
    id: segment.id || fallbackId,
    axis: segment.axis,
    fraction: clamp(finiteNumber(segment.fraction, 0.5), designLimits.handleOffset.min, designLimits.handleOffset.max),
    startFraction,
    endFraction
  };
}

function isWallFace(face: string): face is WallFace {
  return face === "front" || face === "back" || face === "left" || face === "right";
}
